import type { AxiosInstance } from 'axios';
import authService from './auth.service';
import { toText } from '../lib/toText';
import type {
  ReversalListResponse,
  ReversalProposePayload,
  ReversalRequest,
  ReversalStatus,
} from '../types/finance';

interface ListFilters {
  status?: ReversalStatus[];
  withdrawal_id?: string;
  page?: number;
  size?: number;
}

class ReversalsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async propose(payload: ReversalProposePayload): Promise<{ reversal_id: string }> {
    const res = await this.api.post<{ reversal_id: string }>('/api/v1/reversals', payload, {
      headers: { 'Idempotency-Key': crypto.randomUUID() },
    });
    return res.data;
  }

  async list(filters: ListFilters = {}): Promise<ReversalListResponse> {
    const res = await this.api.get<ReversalListResponse>('/api/v1/reversals', {
      params: filters,
      paramsSerializer: (params) => {
        const parts: string[] = [];
        for (const [key, value] of Object.entries(params)) {
          if (value === undefined || value === null) continue;
          if (Array.isArray(value)) {
            for (const v of value) {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(v))}`);
            }
          } else {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(toText(value))}`);
          }
        }
        return parts.join('&');
      },
    });
    return res.data;
  }

  /** Manager approval — carries a fresh TOTP step-up `mfa_code`
   *  (money-moving action). */
  async approve(
    reversalId: string, expectedVersion: number, mfaCode: string,
  ): Promise<{ reversal_id: string; ledger_entry_id: string }> {
    const res = await this.api.post<{ reversal_id: string; ledger_entry_id: string }>(
      `/api/v1/reversals/${reversalId}/approve`,
      { expected_version: expectedVersion, mfa_code: mfaCode },
    );
    return res.data;
  }

  async reject(
    reversalId: string,
    expectedVersion: number,
    reason: string,
  ): Promise<{ reversal_id: string }> {
    const res = await this.api.post<{ reversal_id: string }>(
      `/api/v1/reversals/${reversalId}/reject`,
      { expected_version: expectedVersion, reason },
    );
    return res.data;
  }

  async pendingCount(): Promise<number> {
    const res = await this.list({ status: ['pending'], size: 1 });
    return res.total;
  }

  async getOne(reversalId: string): Promise<ReversalRequest | null> {
    const res = await this.list({ size: 100 });
    return res.items.find((r) => r.id === reversalId) ?? null;
  }
}

export default new ReversalsService();
